// Nota da vistoria e faixa de classificação.
// Protótipo: calcScore (index.html:586) e classify (index.html:601).

import type { ItemChecklist, Respostas, Situacao } from './tipos.js';

/**
 * Nota em % com 1 casa decimal.
 * C, OBS e NA valem o peso cheio; NC e item sem resposta valem 0.
 * Checklist sem peso nenhum vale 100.
 *
 * A soma é feita em ponto flutuante, na ordem dos itens, igual ao protótipo: é isso que
 * garante a mesma nota até a última casa (testado contra as 169 notas do histórico).
 */
export function calcularNota(itens: readonly ItemChecklist[], respostas: Respostas): number {
  let total = 0;
  let ganho = 0;
  for (const it of itens) {
    const s = respostas[it.n]?.s ?? null;
    total += it.peso;
    if (s === 'C' || s === 'OBS' || s === 'NA') ganho += it.peso;
  }
  if (total === 0) return 100;
  return Math.round((ganho / total) * 1000) / 10;
}

export type Faixa = 'muito_bom' | 'bom' | 'regular' | 'insuficiente' | 'anexada';

export interface Classificacao {
  faixa: Faixa;
  rotulo: string;
}

/**
 * Régua única do app (decisão 7.3): a do formulário e do PDF do protótipo.
 * ≥ 95,1 Muito bom · ≥ 90,1 Bom · ≥ 80,1 Regular · abaixo Insuficiente · sem nota = Anexada.
 */
export function classificar(nota: number | null | undefined): Classificacao {
  if (typeof nota !== 'number' || Number.isNaN(nota)) return { faixa: 'anexada', rotulo: 'Anexada' };
  // Médias têm mais casas do que a tela mostra: a faixa segue o valor exibido (1 casa), para que
  // "95.1%" nunca apareça como "Bom". A nota da vistoria já tem 1 casa, então nada muda nela.
  const n = Math.round(nota * 10) / 10;
  if (n >= 95.1) return { faixa: 'muito_bom', rotulo: 'Muito bom' };
  if (n >= 90.1) return { faixa: 'bom', rotulo: 'Bom' };
  if (n >= 80.1) return { faixa: 'regular', rotulo: 'Regular' };
  return { faixa: 'insuficiente', rotulo: 'Insuficiente' };
}

export interface Contagem {
  C: number;
  NC: number;
  NA: number;
  OBS: number;
  semResposta: number;
  total: number;
  respondidos: number;
}

/** Resumo do relatório (protótipo: buildPDFHtml, "Resumo da vistoria"). */
export function contarSituacoes(itens: readonly ItemChecklist[], respostas: Respostas): Contagem {
  const c: Contagem = { C: 0, NC: 0, NA: 0, OBS: 0, semResposta: 0, total: itens.length, respondidos: 0 };
  for (const it of itens) {
    const s: Situacao | undefined = respostas[it.n]?.s;
    if (s) {
      c[s]++;
      c.respondidos++;
    } else {
      c.semResposta++;
    }
  }
  return c;
}
